import React, { useEffect, useRef, useState } from 'react'
import { Spinner, Row, Col, Button } from 'react-bootstrap';
import ReMultiSelect from 'react-select';
import ReMultiSelectAnimated from 'react-select/animated';
import axios from 'axios';

function BulkVariationUpload({ ViewedPage, shop_domain, app_url }) {
    ViewedPage(39);
    const [loading, setLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewHeaders, setPreviewHeaders] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);
    const [viewMode, setViewMode] = useState('live'); // 'live' | 'preview'
    const [saveLoading, setSaveLoading] = useState(false);
    const prevCampaignIdRef = useRef(null);

    const dropdownStyles = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        menu: provided => ({ ...provided, zIndex: 9999 })
    };

    useEffect(() => {
        async function fetchCampaigns() {
            if (!shop_domain) return;
            setLoading(true);
            try {
                const res = await axios.get(`${app_url}/api/store/personalized_campaigns`, { withCredentials: true });
                const data = res?.data?.data || [];
                const opts = (Array.isArray(data) ? data : []).map(c => ({
                    value: c,
                    label: c.campaign_name || c.campaign_id || ''
                }));
                setCampaigns(opts);
            } catch (err) {
                setCampaigns([]);
            } finally {
                setLoading(false);
            }
        }
        fetchCampaigns();
    }, [app_url, shop_domain]);

    const buildHeaders = () => {
        if (!selectedCampaign || !selectedCampaign.value) return ['SXI Content'];
        return Array.isArray(selectedCampaign.value?.templates?.customized_sections)
            ? [
                'SXI Content',
                ...selectedCampaign.value.templates.customized_sections
                    .map(s => {
                        const baseLabel = s.label || '';
                        return s.view === 'mobile' ? `${baseLabel} (Mobile)` : baseLabel;
                    })
                    .filter(Boolean),
            ]
            : ['SXI Content'];
    };

    const handleOpenSheet = async () => {
        if (!selectedCampaign || !selectedCampaign.value) return;
        setSheetLoading(true);
        try {
            const headers = buildHeaders();
            const res = await axios.post(`${app_url}/api/dev/bulk-variation/sheet`, {
                campaign_id: selectedCampaign.value.campaign_id,
                campaign_name: selectedCampaign.value.campaign_name,
                headers
            }, { withCredentials: true });
            const url = res?.data?.spreadsheetUrl || res?.data?.sheetUrl;
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                console.error('No sheet URL returned');
            }
        } catch (err) {
            console.error('Error opening/creating sheet', err);
        } finally {
            setSheetLoading(false);
        }
    };

    useEffect(() => {
        const campaignId = selectedCampaign?.value?.campaign_id || null;
        const campaignChanged = campaignId !== prevCampaignIdRef.current;
        if (campaignChanged) {
            prevCampaignIdRef.current = campaignId;
        }

        if (!selectedCampaign || !selectedCampaign.value) {
            setPreviewHeaders([]);
            setPreviewRows([]);
            setViewMode('live');
            return;
        }

        const bulk = selectedCampaign.value.bulk_variation_upload || {};
        const live = bulk?.live || bulk; // fallback for legacy shape
        const preview = bulk?.preview;

        if (campaignChanged) {
            // New campaign selection: default to live
            setViewMode('live');
            setPreviewHeaders(live?.headers || []);
            setPreviewRows(live?.rows || []);
            return;
        }

        // Same campaign; respect current viewMode
        if (viewMode === 'preview' && preview) {
            setPreviewHeaders(preview.headers || []);
            setPreviewRows(preview.rows || []);
            return;
        }

        setPreviewHeaders(live?.headers || []);
        setPreviewRows(live?.rows || []);
    }, [selectedCampaign, viewMode]);

    const handleGeneratePreview = async () => {
        if (!selectedCampaign || !selectedCampaign.value) return;
        setPreviewLoading(true);
        try {
            const headers = buildHeaders();
            const res = await axios.post(`${app_url}/api/dev/bulk-variation/preview`, {
                campaign_id: selectedCampaign.value.campaign_id,
                campaign_name: selectedCampaign.value.campaign_name,
                headers
            }, { withCredentials: true });
            const data = res?.data?.data || {};
            const saved = res?.data?.saved || {};
            const nextHeaders = saved.preview?.headers || saved.headers || data.headers || headers || [];
            const nextRows = saved.preview?.rows || saved.rows || data.rows || [];
            const nextJson = saved.preview?.json || saved.json || {};
            setPreviewHeaders(nextHeaders);
            setPreviewRows(nextRows);
            setViewMode('preview');
            // Update selectedCampaign local copy with preview
            const existingBulk = selectedCampaign.value.bulk_variation_upload || {};
            const updatedValue = {
                ...selectedCampaign.value,
                bulk_variation_upload: {
                    ...existingBulk,
                    preview: {
                        headers: nextHeaders,
                        rows: nextRows,
                        json: nextJson
                    }
                }
            };
            setSelectedCampaign({
                ...selectedCampaign,
                value: updatedValue
            });
        } catch (err) {
            console.error('Error generating preview', err);
            setPreviewHeaders([]);
            setPreviewRows([]);
            setViewMode('live');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSaveLive = async () => {
        if (!selectedCampaign || !selectedCampaign.value) return;
        const bulk = selectedCampaign.value.bulk_variation_upload || {};
        const preview = bulk.preview;
        if (!preview || !preview.headers || !preview.rows) return;
        setSaveLoading(true);
        try {
            const res = await axios.post(`${app_url}/api/dev/bulk-variation/save`, {
                campaign_id: selectedCampaign.value.campaign_id,
                campaign_name: selectedCampaign.value.campaign_name,
                headers: preview.headers,
                rows: preview.rows
            }, { withCredentials: true });
            const saved = res?.data?.data || {};
            const updatedValue = {
                ...selectedCampaign.value,
                bulk_variation_upload: {
                    ...bulk,
                    live: saved,
                    preview
                }
            };
            setSelectedCampaign({
                ...selectedCampaign,
                value: updatedValue
            });
            setViewMode('live');
            setPreviewHeaders(saved.headers || []);
            setPreviewRows(saved.rows || []);
        } catch (err) {
            console.error('Error saving live data', err);
        } finally {
            setSaveLoading(false);
        }
    };

    return (
        <div className='main-container' style={{ position: "relative" }}>
            {!shop_domain ?
                <div className='go_to_shopify_admin_div'>
                    <h5 style={{ display: 'inline-block' }}>!! Go to Shopify Admin and open our app !!</h5>
                </div>
                :
                <>
                    {loading ?
                        <div className="text-center mt-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                        :
                        <>
                            {/* Original Title and Create Button Structure */}
                            <div className='main-title mt-4 d-flex justify-content-between align-items-center'>
                                <h2>Bulk Variation Upload</h2>
                            </div>


                            <Row className="mt-4">
                                <Col xs={12} md={6}>
                                    <label className="charts_filter_label mb-2">Select Campaign</label>
                                    <ReMultiSelect
                                        className='campaign_summary_custom_section_dropdown'
                                        closeMenuOnSelect={true}
                                        components={ReMultiSelectAnimated()}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={dropdownStyles}
                                        options={campaigns}
                                        value={selectedCampaign}
                                        onChange={setSelectedCampaign}
                                        placeholder="Search campaign..."
                                        isClearable
                                    />
                                </Col>
                                <Col xs={12} md={6} className="d-flex align-items-end gap-2 flex-wrap">
                                    <Button
                                        className='sxi_primary_blue_btn'
                                        disabled={!selectedCampaign || sheetLoading}
                                        onClick={handleOpenSheet}
                                    >
                                        {sheetLoading ? <Spinner size="sm" animation="border" /> : 'Open Sheet'}
                                    </Button>
                                    <Button
                                        className='sxi_secondary_btn'
                                        disabled={!selectedCampaign || previewLoading}
                                        onClick={handleGeneratePreview}
                                    >
                                        {previewLoading ? <Spinner size="sm" animation="border" /> : 'Upload & Preview'}
                                    </Button>
                                    <Button
                                        className='sxi_primary_blue_btn'
                                        variant="success"
                                        disabled={!selectedCampaign || saveLoading || !(selectedCampaign?.value?.bulk_variation_upload?.preview)}
                                        onClick={handleSaveLive}
                                    >
                                        {saveLoading ? <Spinner size="sm" animation="border" /> : 'Save to Live'}
                                    </Button>
                                </Col>
                            </Row>

                            {selectedCampaign?.value?.status && (
                                <div className="mt-2" style={{ fontSize: '0.9rem', color: '#555' }}>
                                    Campaign Status: {String(selectedCampaign.value.status).charAt(0).toUpperCase() + String(selectedCampaign.value.status).slice(1)}
                                </div>
                            )}


                            { selectedCampaign && 
                                <div className="mt-4">
                                    <div className="table-responsive">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div>
                                                <strong>Showing:</strong> {viewMode === 'preview' ? 'Preview' : 'Live'}
                                            </div>
                                            <div className="btn-group">
                                                <Button
                                                    variant={viewMode === 'live' ? 'primary' : 'outline-primary'}
                                                    size="sm"
                                                    onClick={() => {
                                                        const bulk = selectedCampaign?.value?.bulk_variation_upload || {};
                                                        const live = bulk.live || bulk;
                                                        setViewMode('live');
                                                        setPreviewHeaders(live?.headers || []);
                                                        setPreviewRows(live?.rows || []);
                                                    }}
                                                >
                                                    Live
                                                </Button>
                                                <Button
                                                    variant={viewMode === 'preview' ? 'primary' : 'outline-primary'}
                                                    size="sm"
                                                    onClick={() => {
                                                        const bulk = selectedCampaign?.value?.bulk_variation_upload || {};
                                                        const preview = bulk.preview;
                                                        setViewMode('preview');
                                                        setPreviewHeaders(preview?.headers || []);
                                                        setPreviewRows(preview?.rows || []);
                                                    }}
                                                    disabled={!(selectedCampaign?.value?.bulk_variation_upload?.preview)}
                                                >
                                                    Preview
                                                </Button>
                                            </div>
                                        </div>
                                        <table className="table table-bordered table-striped">
                                            <thead>
                                                <tr>
                                                    {previewHeaders.map((h, idx) => <th key={idx}>{h}</th>)}
                                                    {previewHeaders.length > 0 && <th>Preview URL</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewRows.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={previewHeaders.length + 1} className="text-center">No rows with values</td>
                                                    </tr>
                                                ) : (
                                                    previewRows.map((row, rIdx) => (
                                                        <tr key={rIdx}>
                                                            {previewHeaders.map((_, cIdx) => (
                                                                <td key={cIdx}>{row[cIdx] || ''}</td>
                                                            ))}
                                                            <td>
                                                                {selectedCampaign?.value?.landing_page_urls && row[0] ? (
                                                                    <a
                                                                        href={encodeURI(`${selectedCampaign.value.landing_page_urls}?sxi_content=${viewMode === 'preview' ? `${row[0]}_(draft)` : row[0]}`)}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        Preview URL
                                                                    </a>
                                                                ) : null}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                        </>
                    }
                </>
            }
        </div>
    )
}

export default BulkVariationUpload;
